import {useCallback, useEffect, useMemo, useState} from 'react';
import {useHistory} from "react-router-dom";
import {useFrontContext} from "v4/contexts/FrontContext";
import {useProspectPageContext} from "v4/contexts/ProspectPageContext";
import {PROSPECT_ITEM_FORM} from "v4/data/apiRoutes";
import { resolveUrl } from "v4/features/front/navigation/utils/route.utils";
import {useNavigation} from "v4/features/front/navigation/contexts/NavigationContext";
import useFetch from "v4/hooks/useFetch";
import {usePolTranslation} from "v4/hooks/usePolTranslation";
import useToggle from "v4/hooks/useToggle";
import {generateUrl} from "v4/services/api.service";

export default function useProspectView({isExpanded, toggleExpanded}) {
    const {t} = usePolTranslation();
    const [isEditMode, toggleEditMode] = useToggle();
    const [prospectHeaderInfos, setProspectHeaderInfos] = useState(null);
    const {addLastVisit} = useFrontContext();
    const {prospectId, setMapProspect, setProspect} = useProspectPageContext();
    const { getDefaultProspectTab, currentRoute } = useNavigation();

    useEffect(() => {
        if (isEditMode && !isExpanded) toggleExpanded();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditMode]);

    const onGetData = useCallback((data) => {
        const hydraMember = data?.['hydra:member'];
        if (hydraMember) {
            if (currentRoute?.route?.native_identifier !== 'detail_contacts') {
                addLastVisit({
                    name: hydraMember.metadata.fullname ?? t('Prospect'),
                    prospectId: prospectId,
                    type: hydraMember.metadata.isBusiness ? 'Entreprise' : 'Particulier',
                    url: `/${resolveUrl(getDefaultProspectTab()?.url, {prospect: prospectId})}`,
                });
            }
            const [prospect, prospectForm] = Object.values(hydraMember.results).reduce((acc, {properties}) => {
                properties.forEach(input => {
                    acc[0][input.key] = input.value;
                    acc[1][input.key] = input;
                })
                return acc;
            }, [{}, {}]);
            prospect.metadata = {prospectForm, ...hydraMember.metadata};
            setProspect(prospect);
            setMapProspect(hydraMember);
            setProspectHeaderInfos(hydraMember.metadata);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentRoute, prospectId]);

    useEffect(() => {
        if (!isExpanded && isEditMode) toggleEditMode();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isExpanded]);

    const {replace, location: {state}} = useHistory();
    const whenSubmitted = useCallback(() => {
        toggleEditMode();
        replace({state: {hasToRefresh: true}})
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [{data, isError}, fetchProspectForSubscriptions] = useFetch();
    useEffect(() => {
        const {verifyProspectSubscriptions, ...restState} = state ?? {};
        if (verifyProspectSubscriptions) {
            fetchProspectForSubscriptions({
                url: generateUrl(PROSPECT_ITEM_FORM, {id: prospectId}),
            });
            replace({state: restState});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state]);

    useEffect(() => {
        if (data?.['hydra:member'] && !isError) {
            setProspectHeaderInfos(data?.['hydra:member'].metadata);
        }
    }, [data, isError]);

    const fetchFormParameters = useMemo(() => ({
        id: prospectId
    }), [prospectId]);

    return {
        isEditMode,
        toggleEditMode,
        prospectHeaderInfos,
        prospectId,
        onGetData,
        whenSubmitted,
        fetchFormParameters
    };
}
